import * as React from 'react';
import { apiError } from '@/lib/api-error';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Loader2, PartyPopper, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import TierChooser from '@/components/billing/TierChooser';
import WizardSteps from '@/components/onboarding/WizardSteps';
import ConfirmEmailStep from '@/components/onboarding/ConfirmEmailStep';
import NewOrgNotice from '@/components/onboarding/NewOrgNotice';
import { ORGS_QUERY_KEY } from '@/hooks/useOrgsQuery';
import { switchOrgAction } from '@/components/organizations/actions';
import {
  chooseAdvisor,
  PRICING_TIERS, createOnboardingOrganization, forgetPricingTier, recallPricingTier, rememberPricingTier,
  fetchEmailVerification,
  fetchOnboardingState,
} from '@/data/billing';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const [step, setStep] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [orgName, setOrgName] = React.useState('');
  const [tier, setTier] = React.useState(recallPricingTier);
  const chosenTier = PRICING_TIERS.find((option) => option.slug === tier);
  const [, setOrgId] = React.useState<string | null>(null);
  const [trialEnds, setTrialEnds] = React.useState<string | null>(null);
  const [daysLeft, setDaysLeft] = React.useState<number | null>(null);

  const preselectedAdvisor = params.get('advisor');

  React.useEffect(() => {
    Promise.all([
      fetchEmailVerification().catch(() => ({ verified: false })),
      fetchOnboardingState().catch(() => null),
    ]).then(([verification, state]) => {
      if (!verification.verified) {
        setStep(0);
        return;
      }
      if (!state?.has_profile) {
        setStep(1);
        return;
      }
      setOrgName(state.organization_name ?? '');
      setTrialEnds(state.trial_ends_at ?? null);
      setDaysLeft(state.days_left ?? null);
      setStep(3);
    });
  }, []);

  const createOrg = async () => {
    if (!orgName.trim()) return;
    setBusy(true);
    try {
      const org = await createOnboardingOrganization(orgName.trim(), tier);
      forgetPricingTier();
      setOrgId(org.id);
      setTrialEnds(org.trial_ends_at);
      setDaysLeft(org.days_left);
      await switchOrgAction({ orgId: org.id });
      await queryClient.invalidateQueries({ queryKey: ORGS_QUERY_KEY });

      if (preselectedAdvisor) {
        await chooseAdvisor(preselectedAdvisor, org.id).catch(() => undefined);
      }

      setStep(3);
    } catch (error) {
      toast.error(apiError(error, 'Could not create your organisation.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-3xl space-y-6" data-testid="onboarding-page">
        <WizardSteps current={step + 1} />

        {step === 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Confirm your email</CardTitle>
              <CardDescription>
                So we know we can reach you, and so no one can sign up with someone else's address.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ConfirmEmailStep onConfirmed={() => setStep(1)} />
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card data-testid="onboarding-plan">
            <CardHeader>
              <CardTitle>Choose your plan</CardTitle>
              <CardDescription>
                It decides what you pay when the 30-day trial ends. Every plan
                starts with the same free trial, and BitSwan can move you later
                if you pick the wrong one.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <TierChooser
                value={tier}
                onChange={(slug) => {
                  setTier(slug);
                  rememberPricingTier(slug);
                }}
              />
              <Button onClick={() => setStep(2)} data-testid="onboarding-plan-continue">
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <>
            <NewOrgNotice />
            <Card>
              <CardHeader>
                <CardTitle>Name your organisation</CardTitle>
                <CardDescription>
                  This is the organisation your colleagues will join and the name we
                  will show on your account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organisation name</Label>
                  <Input
                    id="org-name"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createOrg()}
                    placeholder="Meridian Foods"
                    data-testid="onboarding-org-name"
                    autoFocus
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="onboarding-back">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={createOrg}
                    disabled={busy || !orgName.trim()}
                    data-testid="onboarding-create-org"
                  >
                    {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Start my 30-day trial on {chosenTier?.name ?? 'Small business'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {step === 3 && (
          <Card data-testid="onboarding-done">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PartyPopper className="h-5 w-5 text-amber-500" />
                You are set up
              </CardTitle>
              <CardDescription>
                {trialEnds
                  ? `Your 30-day trial runs until ${new Date(trialEnds).toLocaleDateString()}.`
                  : 'Your 30-day trial has started.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm" data-testid="onboarding-trial-summary">
                {daysLeft ?? 30} days left · one Bailey server included
              </p>
              <p className="text-sm text-muted-foreground">
                Nothing to pay yet. When you are ready to keep going past the
                trial, add your billing details. Whenever you want help setting up your first automations, pick an automation advisor from <strong>Your advisor</strong> in the sidebar.
              </p>
              <Button
                onClick={() => navigate('/dashboard', { replace: true })}
                data-testid="onboarding-go-to-dashboard"
              >
                Go to your dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
